import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../commonDesign/SydInput'
import SydSwitch from '../../../../../../commonDesign/SydSwitch'
import SydButton from '../../../../../../commonDesign/Button'

function SelectOptionsEditor ({ options, onChange }) {
  const handleAdd = useCallback(() => {
    onChange([...(options || []), { label: '', value: '' }])
  }, [options, onChange])

  const handleRemove = useCallback((index) => {
    onChange((options || []).filter((_, i) => i !== index))
  }, [options, onChange])

  const handleUpdate = useCallback((index, key, value) => {
    const updated = [...(options || [])]
    updated[index] = { ...updated[index], [key]: value }
    onChange(updated)
  }, [options, onChange])

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-color-subtle)' }}>Options</span>
          <SydButton variant='ghost' size='xs' icon='add' onClick={handleAdd}>Add</SydButton>
        </div>
      </Grid>
      {(options || []).map((opt, index) => (
        <React.Fragment key={index}>
          <Grid item xs={4}>
            <SydLabel label={index === 0 ? 'Label' : undefined}>
              <SydInput value={opt.label || ''} onChange={(e) => handleUpdate(index, 'label', e.target.value)} placeholder='Display label' />
            </SydLabel>
          </Grid>
          <Grid item xs={3}>
            <SydLabel label={index === 0 ? 'Value' : undefined}>
              <SydInput value={opt.value ?? ''} onChange={(e) => handleUpdate(index, 'value', e.target.value)} placeholder='Value' />
            </SydLabel>
          </Grid>
          <Grid item xs={4}>
            <SydLabel label={index === 0 ? 'Description' : undefined}>
              <SydInput value={opt.description || ''} onChange={(e) => handleUpdate(index, 'description', e.target.value)} placeholder='Optional help text' />
            </SydLabel>
          </Grid>
          <Grid item xs={1} style={{ display: 'flex', alignItems: index === 0 ? 'flex-end' : 'center', paddingBottom: '4px' }}>
            <SydButton variant='ghost' size='xs' icon='delete' onClick={() => handleRemove(index)} />
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  )
}

SelectOptionsEditor.propTypes = {
  options: PropTypes.array,
  onChange: PropTypes.func.isRequired
}

function FieldTypeConfig ({ field, onChange, sectionClass, sectionLabelClass, dividerClass }) {
  const type = field.editorType

  const updateField = useCallback((key, value) => {
    onChange({ ...field, [key]: value })
  }, [field, onChange])

  if (type === 'select') {
    return (
      <>
        {dividerClass && <div className={dividerClass} />}
        <div className={sectionClass}>
          <div className={sectionLabelClass}>Select Configuration</div>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <SydLabel label='Allow Multiple'>
                <SydSwitch value={!!field.multiple} onChange={(val) => updateField('multiple', val)} />
              </SydLabel>
            </Grid>
          </Grid>
          <div style={{ marginTop: '8px' }}>
            <SelectOptionsEditor
              options={field.editorOptions || field.options || []}
              onChange={(opts) => onChange({ ...field, editorOptions: opts })}
            />
          </div>
        </div>
      </>
    )
  }

  if (type === 'textarea') {
    return (
      <>
        {dividerClass && <div className={dividerClass} />}
        <div className={sectionClass}>
          <div className={sectionLabelClass}>Text Area Configuration</div>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <SydLabel label='Rows'>
                <SydInput
                  type='number'
                  value={field.rows ?? 4}
                  onChange={(e) => updateField('rows', parseInt(e.target.value) || 4)}
                />
              </SydLabel>
            </Grid>
          </Grid>
        </div>
      </>
    )
  }

  if (type === 'hidden') {
    return (
      <>
        {dividerClass && <div className={dividerClass} />}
        <div className={sectionClass}>
          <div className={sectionLabelClass}>Hidden Field Configuration</div>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <SydLabel label='Value' description='Static value submitted with the form. Can also be set via Init From or Default.'>
                <SydInput value={field.value ?? ''} onChange={(e) => updateField('value', e.target.value)} placeholder='Static value' />
              </SydLabel>
            </Grid>
          </Grid>
        </div>
      </>
    )
  }

  if (type === 'readonly-description') {
    return (
      <>
        {dividerClass && <div className={dividerClass} />}
        <div className={sectionClass}>
          <div className={sectionLabelClass}>Readonly Description Configuration</div>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <SydLabel label='Display Value' description='Static text displayed to the user (read-only)'>
                <SydInput value={field.value ?? ''} onChange={(e) => updateField('value', e.target.value)} placeholder='Text to display' />
              </SydLabel>
            </Grid>
          </Grid>
        </div>
      </>
    )
  }

  if (type === 'readonly-accessor') {
    return (
      <>
        {dividerClass && <div className={dividerClass} />}
        <div className={sectionClass}>
          <div className={sectionLabelClass}>Readonly Accessor Configuration</div>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <SydLabel label='Accessor' description='JSON path to read from initialization data'>
                <SydInput value={field.accessor ?? ''} onChange={(e) => updateField('accessor', e.target.value)} placeholder='$.data.fieldName' />
              </SydLabel>
            </Grid>
            <Grid item xs={6}>
              <SydLabel label='Label'>
                <SydInput value={field.label ?? ''} onChange={(e) => updateField('label', e.target.value)} placeholder='Display label' />
              </SydLabel>
            </Grid>
          </Grid>
        </div>
      </>
    )
  }

  if (type === 'appcontext') {
    return (
      <>
        {dividerClass && <div className={dividerClass} />}
        <div className={sectionClass}>
          <div className={sectionLabelClass}>App Context Configuration</div>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <SydLabel label='Context Key' description='Key from the application context (e.g. clientId, userId). The value is injected automatically.'>
                <SydInput value={field.value ?? ''} onChange={(e) => updateField('value', e.target.value)} placeholder='e.g. clientId' />
              </SydLabel>
            </Grid>
          </Grid>
        </div>
      </>
    )
  }

  if (type === 'markdown') {
    return (
      <>
        {dividerClass && <div className={dividerClass} />}
        <div className={sectionClass}>
          <div className={sectionLabelClass}>Markdown Configuration</div>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <SydLabel label='Placeholder'>
                <SydInput value={field.placeholder ?? ''} onChange={(e) => updateField('placeholder', e.target.value)} placeholder='Placeholder text' />
              </SydLabel>
            </Grid>
          </Grid>
        </div>
      </>
    )
  }

  return null
}

FieldTypeConfig.propTypes = {
  field: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  sectionClass: PropTypes.string,
  sectionLabelClass: PropTypes.string,
  dividerClass: PropTypes.string
}

export default FieldTypeConfig
